import React, { useEffect, useRef, useState } from 'react';
import { useJobCenter } from './JobCenterContext';
import { JobCenterPanel } from './JobCenterPanel';

/**
 * JobCenterBell
 *
 * Standard trigger next to NotificationBell.
 * - Shows active job count
 * - Opens/closes the standard Job Center popup panel
 */
export function JobCenterBell() {
  // Use try-catch to handle case where provider might not be ready
  let jobCenter;
  try {
    jobCenter = useJobCenter();
  } catch (error) {
    // Provider not ready yet, return null to prevent crash
    console.warn('JobCenterBell: JobCenterProvider not ready', error);
    return null;
  }
  
  const { state, activeJobs, open, close } = jobCenter;
  const [show, setShow] = useState<boolean>(state.is_open);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Keep local UI state in sync with provider state
  useEffect(() => {
    setShow(state.is_open);
  }, [state.is_open]);

  // Click-outside to close
  useEffect(() => {
    const onDocMouseDown = (evt: MouseEvent) => {
      if (!show) return;
      const el = anchorRef.current;
      if (!el) return;
      if (!el.contains(evt.target as Node)) {
        setShow(false);
        close(true);
      }
    };

    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [close, show]);

  const activeCount = activeJobs.length;
  const shouldShowIndicator = activeCount > 0;

  const toggle = () => {
    const next = !show;
    setShow(next);
    if (next) open('user_toggle');
    else close(true);
  };

  return (
    <div ref={anchorRef} className="relative">
      <button
        type="button"
        onClick={toggle}
        className="relative inline-flex items-center justify-center rounded-full p-2 text-ink hover:text-ink hover:bg-surface-2 active:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Job Center"
        aria-expanded={show}
      >
        <span className="text-base font-semibold">Jobs</span>
        {shouldShowIndicator && (
          <span className="ml-2 inline-flex items-center justify-center rounded-full bg-blue-600 px-2 py-0.5 text-xs font-semibold text-white">
            {activeCount}
          </span>
        )}
      </button>

      {show && (
        <div className="fixed left-2 right-2 top-16 bottom-2 z-[9999] sm:absolute sm:left-auto sm:right-0 sm:top-11 sm:bottom-auto sm:w-[360px] sm:max-w-[calc(100vw-1rem)]">
          <JobCenterPanel />
        </div>
      )}
    </div>
  );
}


